import { useState, useRef, useCallback, useEffect } from 'react';
import type { DocumentType } from '../types';

interface CameraCaptureProps {
  onCapture: (blob: Blob, documentType: DocumentType) => Promise<void>;
  isLoading: boolean;
}

const DOCUMENT_TYPES: { value: DocumentType; label: string }[] = [
  { value: 'invoice', label: 'Invoice' },
  { value: 'bill', label: 'Bill' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'handwritten_note', label: 'Handwritten Note' },
  { value: 'camera_capture', label: 'Other' },
];

export function CameraCapture({ onCapture, isLoading }: CameraCaptureProps) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [documentType, setDocumentType] = useState<DocumentType>('invoice');

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const startCamera = useCallback(async () => {
    try {
      setError(null);

      // Stop any existing stream first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsStreaming(true);
      }
    } catch (err) {
      setError('Unable to access camera. Please ensure camera permissions are granted.');
      console.error('Camera error:', err);
    }
  }, [facingMode]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsStreaming(false);
  }, []);

  const switchCamera = useCallback(async () => {
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newMode);

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: newMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (err) {
      setError('Unable to switch camera.');
      console.error('Camera switch error:', err);
    }
  }, [facingMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              setCapturedBlob(blob);
              setCapturedImage(URL.createObjectURL(blob));
              stopCamera();
            }
          },
          'image/jpeg',
          0.9
        );
      }
    }
  }, [stopCamera]);

  const retake = useCallback(() => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
    }
    setCapturedImage(null);
    setCapturedBlob(null);
    startCamera();
  }, [startCamera, capturedImage]);

  const handleSubmit = async () => {
    if (capturedBlob) {
      await onCapture(capturedBlob, documentType);
      if (capturedImage) {
        URL.revokeObjectURL(capturedImage);
      }
      setCapturedImage(null);
      setCapturedBlob(null);
    }
  };

  return (
    <div className="camera-capture">
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Document type selector - always visible */}
      <div className="document-type-selector">
        <label htmlFor="cameraDocType">Document Type:</label>
        <select
          id="cameraDocType"
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value as DocumentType)}
          disabled={isLoading}
        >
          {DOCUMENT_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="error-message">{error}</div>}

      {!isStreaming && !capturedImage && (
        <div className="camera-start">
          <button className="start-camera-btn" onClick={startCamera}>
            <span className="camera-icon">📷</span>
            <span>Open Camera</span>
          </button>
          <p className="hint">
            Point camera at a document and tap the capture button
          </p>
        </div>
      )}

      {isStreaming && (
        <div className="video-container">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="camera-video"
          />

          <div className="camera-controls">
            <button
              className="switch-camera-btn"
              onClick={switchCamera}
              title="Switch Camera"
            >
              🔄
            </button>
            <button
              className="capture-btn"
              onClick={capturePhoto}
              title="Capture"
            >
              <span className="capture-ring"></span>
            </button>
            <button className="cancel-btn" onClick={stopCamera} title="Cancel">
              ✕
            </button>
          </div>
        </div>
      )}

      {capturedImage && (
        <div className="captured-container">
          <img src={capturedImage} alt="Captured" className="captured-image" />
          <div className="captured-actions">
            <button className="retake-btn" onClick={retake} disabled={isLoading}>
              Retake
            </button>
            <button
              className="upload-btn"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Upload & Process'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
